import { useRef, useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const faqs = [
  {
    question: 'How fast can we start?',
    answer:
      'Most engagements begin within 48–72 hours of proposal acceptance. For emergency response, we can have a CISO on call within 24 hours.',
  },
  {
    question: "What if the CXO isn't the right fit?",
    answer:
      "We offer a 2-week trial period for all engagements. If it's not working, we'll help you find a replacement at no additional platform fee.",
  },
  {
    question: 'How are payments handled?',
    answer:
      'Companies pay invoices directly to CXOs. We track payments and release funds based on milestone completion. In Phase 2, we\'ll offer escrow for added security.',
  },
  {
    question: 'Is our data protected?',
    answer:
      'Absolutely. We use AES-256 encryption, are PDPA-compliant (Singapore), GDPR-ready, and SOC 2 Type II certified. Your data stays in APAC regions by default.',
  },
  {
    question: 'Can we switch engagement types mid-project?',
    answer:
      'Yes, with mutual agreement. You can upgrade from Advisory to Fractional, or convert to Project-based with a new SOW.',
  },
  {
    question: 'What regions do you cover?',
    answer:
      'We currently serve Singapore, Hong Kong, Australia, and India. CXOs can work remotely or on-site depending on your requirements.',
  },
];

export default function FAQSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Items stagger
      itemsRef.current.forEach((item, index) => {
        if (item) {
          gsap.fromTo(
            item,
            { y: 18, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.5,
              delay: index * 0.08,
              scrollTrigger: {
                trigger: item,
                start: 'top 90%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-flowing bg-navy relative z-[130]"
    >
      <div className="w-full px-6 lg:px-12 max-w-3xl mx-auto">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-12 opacity-0">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
            FAQ
          </span>
          <h2 className="font-heading text-h2 font-bold text-white">
            Frequently asked questions
          </h2>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              ref={(el) => { itemsRef.current[index] = el; }}
              className="surface-panel overflow-hidden opacity-0"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.04] transition-colors"
              >
                <span className="font-medium text-white pr-4">{faq.question}</span>
                <ChevronDown
                  className={`w-5 h-5 text-slate flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-5 pb-5">
                  <p className="text-slate leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
